import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Paper, Grid, CircularProgress, Alert, Divider, Select, MenuItem, InputLabel, FormControl, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { Store as StoreIcon, Settings as SettingsIcon, Sync as SyncIcon, CloudDone } from '@mui/icons-material';
import api from '../services/api';
import { API_URL } from '../utils/api';

const StoreConnectionPage = () => {
  const [stores, setStores] = useState([]);
  const [gaProperties, setGaProperties] = useState({});
  const [selectedGA, setSelectedGA] = useState({});
  const [gscSites, setGscSites] = useState({});
  const [selectedGSC, setSelectedGSC] = useState({});
  const [connectionStatus, setConnectionStatus] = useState({
    ga: false,
    gsc: false
  });
  const [isLoading, setIsLoading] = useState({
    stores: true,
    ga: false,
    gsc: false,
    saveGA: false,
    saveGSC: false
  });
  const [message, setMessage] = useState({
    type: '',
    text: ''
  });
  const [shopifyShop, setShopifyShop] = useState('innerunion.myshopify.com');
  const [shopifyToken, setShopifyToken] = useState('');
  const [isConnectingShopify, setIsConnectingShopify] = useState(false);

  // Fetch stores on initial load
  useEffect(() => {
    fetchStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check connection status from URL params
  useEffect(() => {
    const url = new URL(window.location.href);
    const connection = url.searchParams.get('connection');
    const storeId = url.searchParams.get('storeId');

    if (connection === 'google_success' && storeId) {
      setMessage({
        type: 'success',
        text: 'Successfully connected to Google. Now you can select your GA property.'
      });
      fetchGAProperties(storeId);
      setConnectionStatus(prev => ({ ...prev, ga: true }));
    } else if (connection === 'gsc_success' && storeId) {
      setMessage({
        type: 'success',
        text: 'Successfully connected to Google Search Console. Now you can select your site.'
      });
      fetchGSCSites(storeId);
      setConnectionStatus(prev => ({ ...prev, gsc: true }));
    } else if (connection === 'google_error') {
      setMessage({
        type: 'error',
        text: 'Failed to connect to Google services. Please try again.'
      });
    }
  }, []);

  const fetchStores = async () => {
    try {
      setIsLoading(prev => ({ ...prev, stores: true }));
      const res = await api.get('/api/stores');
      const data = res.data;
      setStores(data);

      // If we have stores, fetch Google settings for the first store
      if (data.length > 0) {
        checkExistingConnections(data[0].id);
      }
    } catch (err) {
      console.error('Failed to load stores:', err.response?.data || err.message);
      setMessage({
        type: 'error',
        text: 'Failed to load your stores. Please refresh the page.'
      });
    } finally {
      setIsLoading(prev => ({ ...prev, stores: false }));
    }
  };

  const connectCustomShopify = async () => {
    try {
      setIsConnectingShopify(true);
      setMessage({ type: '', text: '' });
      const response = await api.post('/api/stores/manual-connect', {
        shop: shopifyShop,
        accessToken: shopifyToken,
      });
      setShopifyToken('');
      setMessage({ type: 'success', text: `${response.data.store.name} is connected.` });
      await fetchStores();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.error || 'Could not connect Shopify. Please check the shop domain and token.',
      });
    } finally {
      setIsConnectingShopify(false);
    }
  };

  const checkExistingConnections = async (storeId) => {
    try {
      const res = await api.get(`/api/stores/${storeId}/google-settings`);
      const data = res.data;

      // If there's an existing GA property ID, set it in state and fetch available properties
      if (data.gaPropertyId) {
        setSelectedGA(prev => ({ ...prev, [storeId]: data.gaPropertyId }));
        setConnectionStatus(prev => ({ ...prev, ga: true }));
        fetchGAProperties(storeId);
      }

      // If there's an existing GSC site URL, set it in state and fetch available sites
      if (data.gscSiteUrl) {
        setSelectedGSC(prev => ({ ...prev, [storeId]: data.gscSiteUrl }));
        setConnectionStatus(prev => ({ ...prev, gsc: true }));
        fetchGSCSites(storeId);
      }
    } catch (err) {
      console.error('Failed to check existing connections:', err.response?.data || err.message);
    }
  };

  const fetchGAProperties = async (storeId) => {
    try {
      setIsLoading(prev => ({ ...prev, ga: true }));
      const res = await api.get(`/api/google/analytics/properties?storeId=${storeId}`);
      const data = res.data;
      console.log('Available GA properties:', data); // Debug: Log the structure

      // FIXED: Check for both array and object formats that might come from the API
      if (Array.isArray(data)) {
        setGaProperties(prev => ({ ...prev, [storeId]: data }));
      } else if (data && data.properties && Array.isArray(data.properties)) {
        // Handle case where properties are nested in a 'properties' field
        setGaProperties(prev => ({ ...prev, [storeId]: data.properties }));
      } else if (data && typeof data === 'object') {
        // Just in case we get a single property object instead of an array
        setGaProperties(prev => ({ ...prev, [storeId]: [data] }));
      } else {
        console.error('Unexpected data format for GA properties:', data);
        setMessage({
          type: 'error',
          text: 'Received unexpected data format for GA properties'
        });
      }
    } catch (err) {
      console.error('Failed to fetch GA properties:', err.response?.data || err.message);
      setMessage({
        type: 'error',
        text: `Error fetching Google Analytics properties: ${err.response?.data?.error || err.message}`
      });
    } finally {
      setIsLoading(prev => ({ ...prev, ga: false }));
    }
  };

  const fetchGSCSites = async (storeId) => {
    try {
      setIsLoading(prev => ({ ...prev, gsc: true }));
      const res = await api.get(`/api/google/search-console/sites?storeId=${storeId}`);
      const data = res.data;
      console.log('Available GSC sites:', data); // Debug: Log the structure

      if (data.sites && Array.isArray(data.sites)) {
        setGscSites(prev => ({ ...prev, [storeId]: data.sites }));
      } else if (Array.isArray(data)) {
        // FIXED: Handle case where sites are directly in the array
        setGscSites(prev => ({ ...prev, [storeId]: data }));
      } else {
        console.error('Unexpected data format for GSC sites:', data);
        setMessage({
          type: 'error',
          text: 'Received unexpected data format for Search Console sites'
        });
      }
    } catch (err) {
      console.error('Failed to fetch GSC sites:', err.response?.data || err.message);
      setMessage({
        type: 'error',
        text: `Error fetching Search Console sites: ${err.response?.data?.error || err.message}`
      });
    } finally {
      setIsLoading(prev => ({ ...prev, gsc: false }));
    }
  };

  const handleGAConnect = async (storeId) => {
    window.location.href = `${API_URL}/api/google/auth?storeId=${storeId}`;
  };

  const handleGSCConnect = async (storeId) => {
    window.location.href = `${API_URL}/api/google/auth/search-console?storeId=${storeId}`;
  };

  const handleSaveGA = async (storeId) => {
    if (!selectedGA[storeId]) {
      setMessage({
        type: 'error',
        text: 'Please select a Google Analytics property'
      });
      return;
    }

    try {
      setIsLoading(prev => ({ ...prev, saveGA: true }));

      await api.post(`/api/stores/${storeId}/google-settings`, {
        gaPropertyId: selectedGA[storeId],
        gscSiteUrl: selectedGSC[storeId] || null  // Keep the existing GSC selection if any
      });

      setMessage({
        type: 'success',
        text: 'Google Analytics property saved successfully!'
      });
    } catch (err) {
      console.error('Failed to save GA settings:', err.response?.data || err.message);
      setMessage({
        type: 'error',
        text: `Failed to save Google Analytics settings: ${err.response?.data?.error || err.message}`
      });
    } finally {
      setIsLoading(prev => ({ ...prev, saveGA: false }));
    }
  };

  const handleSaveGSC = async (storeId) => {
    if (!selectedGSC[storeId]) {
      setMessage({
        type: 'error',
        text: 'Please select a Search Console site'
      });
      return;
    }

    try {
      setIsLoading(prev => ({ ...prev, saveGSC: true }));

      await api.post(`/api/stores/${storeId}/google-settings`, {
        gscSiteUrl: selectedGSC[storeId],
        gaPropertyId: selectedGA[storeId] || null  // Keep the existing GA selection if any
      });

      setMessage({
        type: 'success',
        text: 'Search Console site saved successfully!'
      });
    } catch (err) {
      console.error('Failed to save GSC settings:', err.response?.data || err.message);
      setMessage({
        type: 'error',
        text: `Failed to save Search Console settings: ${err.response?.data?.error || err.message}`
      });
    } finally {
      setIsLoading(prev => ({ ...prev, saveGSC: false }));
    }
  };

  const getCurrentStore = () => {
    return stores.length > 0 ? stores[0] : null;
  };

  return (
    <Box sx={{ animation: 'fadeIn 0.5s ease-in-out' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Connect Your Stores
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Synchronize your ecommerce stores to unlock dynamic AI product enhancements and keyword metrics.
        </Typography>
      </Box>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 4, borderRadius: 2 }}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Connected Stores Card */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6">Connected Stores</Typography>
              <Typography variant="subtitle2" color="primary">
                {isLoading.stores ? <CircularProgress size={16} /> : `${stores.length} Active`}
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {isLoading.stores ? (
                <Typography color="text.secondary">Loading stores...</Typography>
              ) : stores.length > 0 ? (
                stores.map((store) => (
                  <Box key={store.id} sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(67, 56, 202, 0.05)', border: '1px solid rgba(67, 56, 202, 0.1)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <StoreIcon color="primary" sx={{ fontSize: 32 }} />
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>{store.name}</Typography>
                          <Typography variant="caption" color="text.secondary">Platform: {store.platform}</Typography>
                        </Box>
                      </Box>
                      <CloudDone color="success" />
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary">No stores connected yet.</Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Add New Store Card */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 4, height: '100%' }}>
            <Typography variant="h6" mb={3}>Add New Store</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Connect your private/custom Shopify app with its Admin API access token. The token stays server-side and is tied to your Autolistify account.
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Shopify domain"
              value={shopifyShop}
              onChange={(event) => setShopifyShop(event.target.value)}
              placeholder="your-store.myshopify.com"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Admin API access token"
              type="password"
              value={shopifyToken}
              onChange={(event) => setShopifyToken(event.target.value)}
              autoComplete="off"
              sx={{ mb: 2 }}
            />
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={!shopifyShop.trim() || !shopifyToken.trim() || isConnectingShopify}
              onClick={connectCustomShopify}
              startIcon={isConnectingShopify ? <CircularProgress size={16} color="inherit" /> : <StoreIcon />}
            >
              {isConnectingShopify ? 'Verifying Shopify…' : 'Connect Shopify Store'}
            </Button>
          </Paper>
        </Grid>

        {/* API Connections Card */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 4, height: '100%', position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: 0, right: 0, width: 100, height: 100, bgcolor: 'primary.main', opacity: 0.05, borderRadius: '50%', transform: 'translate(30%, -30%)' }} />
            <Typography variant="h6" mb={3}>Web Analytics</Typography>

            {!getCurrentStore() ? (
              <Typography color="text.secondary">Connect a store to enable API integrations.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Google Integrations</Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleGAConnect(getCurrentStore().id)}
                      startIcon={isLoading.ga ? <CircularProgress size={16} /> : null}
                      color={connectionStatus.ga ? 'success' : 'primary'}
                    >
                      {connectionStatus.ga ? 'Analytics Connected' : 'Connect GA4'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleGSCConnect(getCurrentStore().id)}
                      startIcon={isLoading.gsc ? <CircularProgress size={16} /> : null}
                      color={connectionStatus.gsc ? 'success' : 'primary'}
                    >
                      {connectionStatus.gsc ? 'Search Console Connected' : 'Connect GSC'}
                    </Button>
                  </Box>
                </Box>

                {connectionStatus.ga && (
                  <FormControl fullWidth size="small">
                    <InputLabel>GA4 Property</InputLabel>
                    <Select
                      value={selectedGA[getCurrentStore().id] || ''}
                      label="GA4 Property"
                      onChange={e => setSelectedGA(prev => ({ ...prev, [getCurrentStore().id]: e.target.value }))}
                    >
                      {gaProperties[getCurrentStore().id]?.map(prop => (
                        <MenuItem key={prop.id} value={prop.id.replace('properties/', '')}>
                          {prop.displayName || prop.name || prop.id}
                        </MenuItem>
                      ))}
                    </Select>
                    <Button sx={{ mt: 1 }} size="small" variant="text" onClick={() => handleSaveGA(getCurrentStore().id)}>Save GA Property</Button>
                  </FormControl>
                )}

                {connectionStatus.gsc && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Search Console Site</InputLabel>
                    <Select
                      value={selectedGSC[getCurrentStore().id] || ''}
                      label="Search Console Site"
                      onChange={e => setSelectedGSC(prev => ({ ...prev, [getCurrentStore().id]: e.target.value }))}
                    >
                      {gscSites[getCurrentStore().id]?.map(site => (
                        <MenuItem key={site.siteUrl} value={site.siteUrl}>
                          {site.siteUrl}
                        </MenuItem>
                      ))}
                    </Select>
                    <Button sx={{ mt: 1 }} size="small" variant="text" onClick={() => handleSaveGSC(getCurrentStore().id)}>Save GSC Site</Button>
                  </FormControl>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Sync Settings Configuration */}
        <Grid item xs={12}>
          <Paper sx={{ p: 4, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <SettingsIcon color="primary" />
              <Typography variant="h6">Automation & Sync Settings</Typography>
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Sync Frequency</InputLabel>
                  <Select defaultValue="Every hour" label="Sync Frequency">
                    <MenuItem value="Every 15 minutes">Every 15 minutes</MenuItem>
                    <MenuItem value="Every hour">Every hour</MenuItem>
                    <MenuItem value="Every 6 hours">Every 6 hours</MenuItem>
                    <MenuItem value="Daily">Daily</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Data Retention</InputLabel>
                  <Select defaultValue="6 Months" label="Data Retention">
                    <MenuItem value="1 Month">1 Month</MenuItem>
                    <MenuItem value="3 Months">3 Months</MenuItem>
                    <MenuItem value="6 Months">6 Months</MenuItem>
                    <MenuItem value="1 Year">1 Year</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Backup Versions</InputLabel>
                  <Select defaultValue="Last 10 versions" label="Backup Versions">
                    <MenuItem value="Last 5 versions">Last 5 versions</MenuItem>
                    <MenuItem value="Last 10 versions">Last 10 versions</MenuItem>
                    <MenuItem value="Last 15 versions">Last 15 versions</MenuItem>
                    <MenuItem value="Last 20 versions">Last 20 versions</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel control={<Checkbox defaultChecked color="primary" />} label="Sync product information dynamically" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel control={<Checkbox defaultChecked color="primary" />} label="Sync keyword performance seamlessly" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel control={<Checkbox defaultChecked color="primary" />} label="Sync organic search traffic metrics" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel control={<Checkbox color="primary" />} label="Enable autonomous keyword and image enhancement" />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button variant="contained" size="large">Save Configuration</Button>
                <Button variant="outlined" size="large" startIcon={<SyncIcon />}>Force Background Sync</Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StoreConnectionPage;
